import PropTypes from "prop-types";
import React, { useState, useEffect, useRef } from "react";
import { connect } from "react-redux";
import { LegacyCaseView } from "components/Shared";
import MCaseView from "containers/Homeowner/MCaseView";
import { getCaseRCT, get360Value, getHasE2Value } from "actions/qualityAssurance";
import { Outline } from "components/QualityAssurance";
import FileManager from "containers/Homeowner/MCaseView/FileManager";
import {
  ACE_PDR_CONVERT_TO_APPRAISAL_ANY_STATUS,
  ACE_PDR_CONVERT_TO_APPRAISAL_COMPLETED,
  CASE_FILE_REGENERATE_REPORT_PDF,
  COVERAGE_CARD_VIEW,
  QA_CASE_AUDIT_EDIT,
} from "typeConstants/rights";
import CaseIssues from "components/QualityAssurance/Sections/CaseDetails/CaseIssues/CaseIssues";
import { getCubiCasaSketch } from "requests/homeowner/caseforms";
import { userHasRight } from "selectors/rights";
import { getAvailableExports } from 'requests/caseExports';
import { getAllIncidentsForCase } from 'requests/customerService/index.ts';
import { openGoogleMapsLink } from "components/Shared/Functions";
import { QueueTypeEnum } from "interfaces/Mueller.Business.Models.ts";
import { orderByStartDateDesc } from "components/QualityAssurance/Sections/Outline/QATouches";
import CaseFormDataRevisions from "components/Homeowner/CaseFormDataRevisions";
import responseHandler from "responseHandler";
import { CustomerServiceNotes } from "components/CustomerService";
import { formatNoteDateUpdated, getNoteDetails } from "components/Homeowner/History/DataFormatters";
import { InspectionTypeCodes } from "typeConstants/inspectionTypeCodes.ts";
import { QACaseViewTabOrderPreference } from "typeConstants/preferenceTypes";

const SummaryView = (props) => {
  const mounted = useRef(true);
  const [cubiCasa, setCubiCasa] = useState(null);
  const [availableExports, setAvailableExports] = useState([]);
  const [sections, setSections] = useState([]);
  const [iframeSrc, setIframeSrc] = useState("");
  const [csIncidents, setCsIncidents] = useState(null);

  const {
    qaCase,
    qaView,
    controlIdNumber,
    caseInfo,
    caseSketch,
    caseRCT,
    hasCaseRCT,
    getCaseRCT,
    value360,
    get360Value,
    getHasE2Value,
    hasE2Value,
    summaryViewEnabled,
    canViewCoverageCard,
    updatingCaseIssues,
    caseAuditRequired, 
    token,
    makeToast,
    caseHeaders,
  } = props;

  useEffect(() => {
    getCaseRCT({ ControlNumber: controlIdNumber });
    get360Value(controlIdNumber);
    getHasE2Value(controlIdNumber);
    return () => {mounted.current = false;};
  }, []);

  useEffect(() => {
    if (caseHeaders?.caseInfo?.currentInspectionType.code === InspectionTypeCodes.CLOSE_OUT) {
      fetchCsIncidents();
    }
    else {
      setCsIncidents(null);
    }
  }, [caseInfo]);

  useEffect(() => {
    getSections();
  }, [csIncidents]);

  useEffect(() => {
    getSections();
    getCubiCasaSketch(controlIdNumber, (data) => {
      setCubiCasa(data);
    }, () => {}, mounted);
    getAvailableExports(controlIdNumber, (resp) => {
      setAvailableExports(resp ? resp.availableExportCodes : []);
    }, () => {}, mounted);
    
    setIframeSrc(`/inspectorappviewpolicy.asp?lForceEdit=1&nQAQueue_id=${qaCase.qaQueueId}&QueueTypeId=${qaCase.qaQueueTypeId}&EnterpriseEmbedded=1&ControlIDNumber=${qaCase.controlIdNumber}&SSO=${token}`);
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    getSections();
    setIframeSrc(`/inspectorappviewpolicy.asp?lForceEdit=1&nQAQueue_id=${qaCase.qaQueueId}&QueueTypeId=${qaCase.qaQueueTypeId}&EnterpriseEmbedded=1&ControlIDNumber=${qaCase.controlIdNumber}&SSO=${token}`);    
  }, [controlIdNumber, caseInfo, caseSketch, caseRCT, caseAuditRequired, value360, hasE2Value]);

  const getMostRecentResolvedTouch = () => {
    if (!caseInfo?.qaTouches?.length) return;
    const sortedTouches = [...caseInfo.qaTouches].sort(orderByStartDateDesc);
    return sortedTouches.find(t => t.endDate !== null && t.resolution !== null);
  };

  const showDataRevisionsForRejected = () => {
    if (!caseInfo?.qaTouches?.length) return false;
    const lastResolved = getMostRecentResolvedTouch();
    if (lastResolved?.resolution === "REJECTED") return true;
    return false;
  };

  const showDataRevisionsForPreRelease = () => {
    if (!caseInfo?.qaTouches?.length) return false;
    const lastResolved = getMostRecentResolvedTouch();
    if (lastResolved?.resolution === "COMPLETED PRERELEASE") return true;
    return false;
  };

  const fetchCsIncidents = () => {
    getAllIncidentsForCase(controlIdNumber,
      (notes) => { //success callback
        setCsIncidents(notes);
      },
      () => { //failure callback
        makeToast("Failed to fetch CS Incidents for this case", "error");
      },
      mounted,
    );
  };

  const pushCsIncidentsTab = (sectionsArray) => {
    sectionsArray.push({
      id: "CsIncidents",
      title: "CS Notes",
      formStatus: "clean",
      external: true,
      component:
        (
          <div className="summary-view-cs-notes">
            <CustomerServiceNotes
            controlNumber={controlIdNumber}
            tableData={csIncidents}
            viewOnly={true}
            callback={() => { getAllIncidentsForCase(controlIdNumber); }}
            columnDefinitions={[
                      { name: "Last Updated", dataSource: formatNoteDateUpdated, style: { width: "200px" } },
                      { name: "Status", dataSource: "statusName" },
                      { name: "Call Reason", dataSource: "callReason.name" },
                      { name: "Details", dataSource: getNoteDetails, style: { minWidth: "200px" } },
                      { name: "Contact", dataSource: "personType.name" },
                      { name: "Department", dataSource: "assignedDepartment.name" },
                      { name: "Assigned To", dataSource: "assignedTo" },
                      { name: "Performed By", dataSource: "performedBy" },
                    ]}
          />
          </div>
        )
    });
  };

  const getSections = () => {
    let _sections = [];

    if(caseAuditRequired) {
      _sections.push({
        title: `Case Audit`,
        id: "QACaseAudit",
        formStatus: "clean",
        external: true,
      });          
    }

    if(!summaryViewEnabled) {
      _sections.push({
        title: "Legacy Case View",
        id: "LegacyCaseView",
        formStatus: "clean",
        external: true,
        component: (                  
          <LegacyCaseView
            qaView={qaView}
            src={iframeSrc}
            onLoadFinished={() => { }}
            hidden={summaryViewEnabled}
          />)
      });
      if (caseHeaders?.caseInfo?.currentInspectionType.code === InspectionTypeCodes.CLOSE_OUT)
        pushCsIncidentsTab(_sections);  
    }
    else {
      _sections.push({
        title: `Photos`,
        id: "Photos",
        formStatus: "clean",
        external: true,
      });
      if (showDataRevisionsForPreRelease()) {
        _sections.push({
          title: "Form Data Revisions",
          id: "CaseFormDataRevisions",
          formStatus: "clean",
          external: true,
          component: (<CaseFormDataRevisions 
                        controlNumber={controlIdNumber} 
                        defaultFromFriendlyName="Submitted from Field"
                        defaultToFriendlyName="Submitted for Audit"
                      />)
        });
      }
      _sections.push({
        title: `${controlIdNumber} Outline`,
        id: "Header",
        formStatus: "clean",
        external: true,
        component: (<Outline 
                      controlNumber={controlIdNumber}
                      canViewCoverageCard={canViewCoverageCard} 
                      displayAddressMap={openGoogleMapsLink}
                    />)
      });
      if (caseHeaders?.caseInfo?.currentInspectionType.code === InspectionTypeCodes.CLOSE_OUT) {
        pushCsIncidentsTab(_sections);
      }
      if (showDataRevisionsForRejected()) {
        _sections.push({
          title: "Form Data Revisions",
          id: "CaseFormDataRevisions",
          formStatus: "clean",
          external: true,
          component: (<CaseFormDataRevisions 
                        controlNumber={controlIdNumber} 
                        defaultFromFriendlyName="Rejected to Field"
                        defaultToFriendlyName="Submitted from Field"
                      />)
        });
      }
      if (hasCaseRCT) {
        _sections.push({
          title: "RCT",
          id: "Calc",
          formStatus: "clean",
          external: true,
        });
      }
      if (value360.hasValue) {
        _sections.push({
          title: "360 Value",
          id: "360Value",
          formStatus: "clean",
          external: true,
        });
      }
      if (hasE2Value) {
        _sections.push({
          title: "e2Value",
          id: "e2Value",
          formStatus: "clean",
          external: true,
        });
      }
      if (caseSketch) {
        _sections.push({
          title: `Sketch`,
          id: "Sketch",
          formStatus: "clean",
          external: true,
        });
      }
      if (cubiCasa?.sketchComp) {
        _sections.push({
          title: `CubiCasa`,
          id: "CubiCasa",
          formStatus: "clean",
          external: true,
        });
      }
      _sections.push({
        title: `Case Details`,
        id: "QACaseDetails",
        formStatus: "clean",
        external: true,
        component: (<div />)
      });
      _sections.push({
        title: `Case Issues`,
        id: "CaseIssues",
        formStatus: "clean",
        external: true,
        component: (<CaseIssues controlNumber={controlIdNumber} updatingCaseIssues={updatingCaseIssues} />)
      });
      if(availableExports.length){
        _sections.push({
          title: `Exports`,
          id: "Exports",
          formStatus: "clean",
          external: true
        });
      }
      _sections.push({
        title: "File Manager",
        id:  "FileManager",
        formStatus: "clean",
        external: true,
        component: (
          <FileManager
            controlNumber={controlIdNumber}
            canRegeneratePdf={props.rights.includes(CASE_FILE_REGENERATE_REPORT_PDF)}
          />
          )
      });
      if (props.rights.includes(ACE_PDR_CONVERT_TO_APPRAISAL_ANY_STATUS)
          || props.rights.includes(ACE_PDR_CONVERT_TO_APPRAISAL_COMPLETED)
      ) {
        _sections.push({
          title: "Case Actions",
          id: "Validation",
          formStatus: "clean"
        });
      }
    }
    setSections(_sections);
  };

  let pushToPane = caseAuditRequired
    ? ["QACaseAudit", "Photos"]
    : availableExports.length ? ["Exports", "Photos"] : ["Photos"];
  return (
    <div style={{ display: "flex", height: "100%" }}>
      <MCaseView
        preferenceNameToSaveTabOrder={QACaseViewTabOrderPreference}
        externalFormSet={sections.length ? sections : getSections()}
        forcePushToPane={pushToPane}
        controlNumberOverride={controlIdNumber}
        firstChildInitialSize={"40%"}
      />
    </div>
  );
};

SummaryView.propTypes = {
  qaCase: PropTypes.object,
  qaView: PropTypes.object,
  controlIdNumber: PropTypes.number.isRequired,
  caseInfo: PropTypes.object,
  caseSketch: PropTypes.object,
  caseRCT: PropTypes.object,
  hasCaseRCT: PropTypes.bool,
  getCaseRCT: PropTypes.func,
  value360: PropTypes.object,
  get360Value: PropTypes.func,
  getHasE2Value: PropTypes.func,
  hasE2Value: PropTypes.bool,
  summaryViewEnabled: PropTypes.bool.isRequired,
  rights: PropTypes.array,
  canViewCoverageCard: PropTypes.bool,
  updatingCaseIssues: PropTypes.bool,
  caseAuditRequired: PropTypes.bool,
  token: PropTypes.string,
  makeToast: PropTypes.func,
  caseHeaders: PropTypes.object,
};

const mapStateToProps = state => {
  return {
    summaryViewEnabled: state.qualityAssurance.qaVariables.summaryViewEnabled,
    value360: state.qualityAssurance.value360,
    hasE2Value: state.qualityAssurance.hasE2Value,
    qaCase: state.qualityAssurance.qaCase,
    controlIdNumber: state.qualityAssurance.qaCase.controlIdNumber,
    caseInfo: state.qualityAssurance.qaCase.caseInfo,
    caseSketch: state.qualityAssurance.qaCase.caseSketch,
    caseRCT: state.qualityAssurance.caseRCT,
    hasCaseRCT: state.qualityAssurance.hasCaseRCT,
    rights: state.user.profile.rights,
    canViewCoverageCard: userHasRight(COVERAGE_CARD_VIEW)(state),
    updatingCaseIssues: state.qualityAssurance.updatingCaseIssues,
    caseAuditRequired: state.qualityAssurance.qaCase?.caseAuditRequired 
      && state.qualityAssurance.qaCase?.qaQueueTypeId === QueueTypeEnum.AuditQa
      && userHasRight(QA_CASE_AUDIT_EDIT)(state),
    token: state.user.profile.token,
    caseHeaders: state.caseForms.caseHeader,
  };
};

const mapDispatchToProps = dispatch => ({
  getCaseRCT(payload, callback) {
    dispatch(getCaseRCT(payload, callback));
  },
  get360Value(controlNumber) {
    dispatch(get360Value(controlNumber));
  },
  getHasE2Value(controlNumber) {
    dispatch(getHasE2Value(controlNumber));
  },
  makeToast(text, type) {
      responseHandler.customToast(text, type, dispatch);
    },
});

export default connect(mapStateToProps, mapDispatchToProps)(SummaryView);
